import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useUserPreferences } from "../contexts/UserPreferencesContext";
import { translations } from "../config/translations";
import AnswerComponent from "./AnswerComponent";

const UserPanel = () => {
  const auth = useAuth();
  const { language, theme } = useUserPreferences();
  const t = translations[language];
  const isDark = theme === "dark";

  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [question, setQuestion] = useState("");
  const [conversations, setConversations] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const questionInputRef = useRef(null);
  const conversationContainerRef = useRef(null); // conversation container

  // Osobne endpointy dla różnych operacji
  const API_BASE = "https://9mz6n7zlkl.execute-api.eu-central-1.amazonaws.com";
  const QUESTIONS_ENDPOINT = `${API_BASE}/user_questions`;
  const CONVERSATIONS_ENDPOINT = `${API_BASE}/user/questions`;
  const MODELS_ENDPOINT =
    "https://fa2gzurc3l.execute-api.eu-central-1.amazonaws.com/api/models";

  // Pobieranie modeli przypisanych do użytkownika
  useEffect(() => {
    const fetchAssignedModels = async () => {
      if (!auth.isAuthenticated) return;

      try {
        // Najpierw pobierz dane użytkownika, aby uzyskać przypisane modele
        const userResponse = await fetch(
          "https://x0mhcopby7.execute-api.eu-central-1.amazonaws.com/UserManagementFunction",
          {
            headers: {
              Authorization: `Bearer ${auth.user.access_token}`,
            },
          }
        );

        if (!userResponse.ok) {
          throw new Error("Failed to fetch user data");
        }

        const userData = await userResponse.json();
        const currentUser = userData.find(
          (user) => user.username === auth.user.profile["cognito:username"]
        );

        if (!currentUser) {
          throw new Error("User not found");
        }

        // Następnie pobierz wszystkie modele
        const modelsResponse = await fetch(MODELS_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${auth.user.access_token}`,
          },
        });

        if (!modelsResponse.ok) {
          throw new Error("Failed to fetch models");
        }

        const allModels = await modelsResponse.json();

        // Filtruj tylko te modele, które są przypisane do użytkownika
        const assignedModelNames =
          currentUser.AssignedModels?.map((model) => model.S) || [];
        const userModels = allModels.filter((model) =>
          assignedModelNames.includes(model.name)
        );
        setModels(userModels);

        // Auto-select if there's only one model
        if (userModels.length === 1) {
          setSelectedModel(userModels[0]);
        }

        setError(null);
      } catch (error) {
        console.error("Error fetching models:", error);
        setError(t.error);
      }
    };

    fetchAssignedModels();
  }, [auth.isAuthenticated, auth.user, t]);

  // Pobieranie historii konwersacji
  useEffect(() => {
    const fetchConversations = async () => {
      if (!auth.isAuthenticated) return;

      try {
        const response = await fetch(CONVERSATIONS_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${auth.user.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setConversations(
          data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        );
        setError(null);
      } catch (error) {
        console.error("Error fetching conversations:", error);
        setError(t.error);
      }
    };

    fetchConversations();
  }, [auth.isAuthenticated, auth.user, t, CONVERSATIONS_ENDPOINT]);

  useEffect(() => {
    if (selectedModel && questionInputRef.current) {
      questionInputRef.current.focus();
    }
  }, [selectedModel]);

  useEffect(() => {
    if (conversationContainerRef.current) {
      conversationContainerRef.current.scrollTop = 0;
    }
  }, [conversations]);

  useEffect(() => {
    if (models.length === 1 && !selectedModel) {
      setSelectedModel(models[0]);
    }
  }, [models, selectedModel]);

  // useEffect(() => {
  //   if (!auth.isAuthenticated) return;

  //   const fetchAssignedModels = async () => {
  //     try {
  //       const modelsEndpoint = auth.user?.profile["cognito:groups"]?.includes(
  //         "Admins"
  //       )
  //         ? "https://fa2gzurc3l.execute-api.eu-central-1.amazonaws.com/admin/models"
  //         : "https://fa2gzurc3l.execute-api.eu-central-1.amazonaws.com/api/models";

  //       const userResponse = await fetch(
  //         "https://x0mhcopby7.execute-api.eu-central-1.amazonaws.com/UserManagementFunction",
  //         {
  //           headers: {
  //             Authorization: `Bearer ${auth.user.access_token}`,
  //           },
  //         }
  //       );
  //       const userData = await userResponse.json();
  //       const currentUser = userData.find(
  //         (user) => user.username === auth.user.profile["cognito:username"]
  //       );

  //       if (!currentUser) {
  //         throw new Error(t.error);
  //       }

  //       const modelsResponse = await fetch(modelsEndpoint, {
  //         headers: {
  //           Authorization: `Bearer ${auth.user.access_token}`,
  //         },
  //       });
  //       const allModels = await modelsResponse.json();

  //       const assignedModelNames =
  //         currentUser.AssignedModels?.map((model) => model.S) || [];
  //       const userModels = allModels.filter((model) =>
  //         assignedModelNames.includes(model.name)
  //       );

  //       setModels(userModels);
  //       setError(null);
  //     } catch (error) {
  //       console.error("Error fetching assigned models:", error);
  //       setError(t.error);
  //     }
  //   };

  //   fetchAssignedModels();
  // }, [auth.isAuthenticated, auth.user, t]);

  // Obsługa wysyłania pytania
  const handleAskQuestion = async () => {
    if (!selectedModel || !question.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(QUESTIONS_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.user.access_token}`,
        },
        body: JSON.stringify({
          modelId: selectedModel.modelId,
          question: question.trim(),
          userId: auth.user.profile.sub,
          //auth.user.profile["cognito:username"],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      // Dodaj nową konwersację na początek listy
      setConversations((prev) => [
        {
          question: data.question,
          answer: data.answer,
          timestamp: data.timestamp,
          modelName: data.modelName,
          userId: auth.user.profile.sub,
        },
        ...prev,
      ]);

      setQuestion("");
    } catch (error) {
      console.error("Error sending question:", error);
      setError(error.message || t.error);
    } finally {
      setIsLoading(false);
    }
  };

  if (auth.isLoading) {
    return <div>{t.loading}</div>;
  }

  if (!auth.isAuthenticated) {
    return <div>{t.signIn}</div>;
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // zapobiega dodaniu nowej linii
      if (selectedModel && question.trim() && !isLoading) {
        handleAskQuestion();
      }
    }
  };

  return (
    <div className="p-6">
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        <div>
          <h2
            className={`text-xl font-semibold mb-4 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {t.availableModels}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {models.map((model) => (
              <div
                key={model.modelId}
                className={`p-4 border rounded cursor-pointer transition-colors ${
                  selectedModel?.modelId === model.modelId
                    ? isDark
                      ? "bg-blue-900 border-blue-700 text-blue-100"
                      : "bg-blue-100 border-blue-500"
                    : isDark
                    ? "border-gray-600 hover:bg-gray-700"
                    : "hover:bg-blue-50"
                }`}
                onClick={() => setSelectedModel(model)}
              >
                <h3
                  className={`font-semibold ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  {model.name}
                </h3>
              </div>
            ))}
            {models.length === 0 && (
              <div
                className={`col-span-2 text-center py-4 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {t.noModelsAssigned}
              </div>
            )}
          </div>

          <div className="mt-6">
            <h2
              className={`text-xl font-semibold mb-4 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {t.askQuestion}
            </h2>
            <div className="flex gap-2">
              <input
                type="text"
                ref={questionInputRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown} // dodajemy obsługę klawisza
                placeholder={t.typeQuestion}
                className={`flex-1 p-2 rounded ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "border-gray-300"
                } ${isLoading ? "opacity-50" : ""}`}
                disabled={!selectedModel || isLoading}
              />
              <button
                onClick={handleAskQuestion}
                disabled={!selectedModel || !question.trim() || isLoading}
                className={`px-4 py-2 rounded transition-colors ${
                  !selectedModel || !question.trim() || isLoading
                    ? "bg-gray-300 cursor-not-allowed"
                    : isDark
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                }`}
              >
                {isLoading ? t.sending : t.ask}
              </button>
            </div>
          </div>
        </div>

        <div>
          <h2
            className={`text-xl font-semibold mb-4 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {t.conversationHistory}
          </h2>
          <div
            ref={conversationContainerRef}
            className={`border rounded p-4 max-h-[600px] overflow-y-auto ${
              isDark ? "border-gray-600 bg-gray-800" : "border-gray-200"
            }`}
          >
            {conversations.map((conv, index) => (
              <div
                key={index}
                className={`mb-4 pb-4 ${
                  index !== conversations.length - 1
                    ? isDark
                      ? "border-b border-gray-700"
                      : "border-b"
                    : ""
                }`}
              >
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {new Date(conv.timestamp).toLocaleString(
                    language === "PL" ? "pl-PL" : "en-US"
                  )}
                </p>
                <p
                  className={`font-semibold mt-2 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  Q: {conv.question}
                </p>
                <AnswerComponent answer={conv.answer} isDark={isDark} />
              </div>
            ))}
            {conversations.length === 0 && (
              <p
                className={`text-center ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {t.noConversations}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPanel;
